import { useLocalization } from "@tm/localization"
import { useActions } from "@tm/morpheus"
import { useSelector } from "react-redux"
import { createSelector } from "reselect"
import { useState } from "react"
import { Box, Button, Dialog, Icon, Slider, styled, Typography } from "@tm/components"
import { getBundleParams } from "@bundles/wheels/utils"
import { useResponsiveWindowDimension } from "@bundles/wheels/data/hooks"
import { Color } from "../../../data/model"
import { MainState } from "../../main"
import { Actions } from "../business"

const StyledSlider = styled(Slider)(() => ({
    width: "80%",
    marginLeft: "10px",
}))

const StyledDialog = styled(Dialog)<{ isLKQ: boolean }>(({ theme, isLKQ }) => ({
    "& .MuiDialog-paper": {
        position: "absolute",
        top: isLKQ ? "10em" : "12em",
        right: isLKQ ? "-.8em" : "unset",
        left: isLKQ ? "unset" : "-.8em",
        width: "auto",
        maxWidth: "620px",
        borderRadius: 2,
        border: `1px solid ${theme.palette.primary.main}`,
    },
}))

const StyledBoxColor = styled(Box)<{ isActive: boolean }>(({ theme, isActive }) => ({
    width: "30%",
    padding: 1,
    cursor: "pointer",
    border: isActive ? `1px solid ${theme.palette.primary.main}` : "1px solid transparent",
    borderRadius: "8px",
    boxShadow: "0 0 10px rgba(0, 0, 0, 0.15)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    transition: "border-color 0.2s",
    "&:hover": {
        borderColor: "#aaa",
    },
}))

const StyledTypography = styled(Typography)(() => ({
    marginTop: 3,
    overflow: "hidden",
    width: "95%",
}))

const selector = createSelector(
    (s: MainState) => ({
        withSlider: s.wheelsList.configurator.show3D,
        colors: s.wheelsList.configurator.colors,
        configuratorHeight: s.wheelsList.configurator.height,
        configuratorTint: s.wheelsList.configurator.tint,
        selectedColor: s.wheelsList.configurator.selectedColor,
    }),
    (x) => x
)

export default function RimSettingsDialog() {
    const { translateText } = useLocalization()
    const { colors, withSlider, configuratorHeight, configuratorTint, selectedColor } = useSelector(selector)
    const actions = useActions(
        Actions,
        "changeConfiguratorColor",
        "changeRimInfoAndSettingVisibility",
        "changeConfiguratorTint",
        "changeConfiguratorHeight"
    )
    const isLKQ = getBundleParams()?.version === "LKQ"
    const [tintSliderValue, setTintSliderValue] = useState(configuratorTint / 10)
    const [heightSliderValue, setHeightSliderValue] = useState(-configuratorHeight)

    const maxHeight = useResponsiveWindowDimension()

    const handleColorSelect = (color: string) => {
        actions.changeConfiguratorColor(color)
    }

    const handleButtonValue = (value: number) => {
        setHeightSliderValue(value)
        const newHeight = -value
        actions.changeConfiguratorHeight(newHeight)
    }

    const handleTintChange = (value: number) => {
        setTintSliderValue(value)
        const newTint = 10 * value
        actions.changeConfiguratorTint(newTint)
    }

    const renderColor = (color: Color, idx: number) => {
        const isActive = selectedColor === color.code

        return (
            <StyledBoxColor key={idx} onClick={() => handleColorSelect(color.code)} isActive={isActive}>
                <Box
                    sx={{
                        backgroundColor: `#${color.code}`,
                        width: "90%",
                        height: "70px",
                        borderRadius: "4px",
                        marginTop: 1,
                    }}
                />
                <StyledTypography variant="body2" title={color.name}>
                    {color.name}
                </StyledTypography>
            </StyledBoxColor>
        )
    }

    const onClose = () => {
        actions.changeRimInfoAndSettingVisibility(false, false)
    }

    return (
        <StyledDialog
            open
            onOutsideClick={onClose}
            fullWidth
            isLKQ={isLKQ}
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: "transparent",
                        backdropFilter: "none",
                    },
                },
            }}
        >
            <Box display="flex" justifyContent="space-between" alignItems="center" paddingBottom="1em">
                <Typography fontSize={20}>{translateText(943)}</Typography>
                <Button variant="text" onClick={onClose} startIcon={<Icon name="close" />} />
            </Box>
            <Box display="flex" sx={{ maxHeight }} gap="1em">
                {withSlider && (
                    <Box paddingLeft="1em">
                        <Box>
                            <Typography>{translateText(995)}</Typography>
                            <StyledSlider max={12} min={0} value={heightSliderValue} onChange={(_, value) => handleButtonValue(value as number)} />
                            <Box display="flex" justifyContent="space-around">
                                {[0, 4, 8, 12].map((val) => (
                                    <Button key={val} variant="text" onClick={() => handleButtonValue(val)}>
                                        {val}
                                    </Button>
                                ))}
                            </Box>
                        </Box>
                        <Box>
                            <Typography>{translateText(1597)}</Typography>
                            <StyledSlider max={10} min={0} value={tintSliderValue} onChange={(_, value) => handleTintChange(value as number)} />
                        </Box>
                    </Box>
                )}

                <Box display="flex" flexWrap="wrap" gap="1em" padding="5px" overflow="auto">
                    {colors.map(renderColor)}
                </Box>
            </Box>
        </StyledDialog>
    )
}
